import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./index.css";
import AssetsModal from "../AssetsModal.js";
import { FaEdit, FaTrash } from "react-icons/fa";
import Loader from "../../Components/Loader";
import GeneralToast from "../../Components/Toaster";
import Select from "react-select";
import { OnStatusOptions } from "../../Utils/constants";
import { useCreateCollectionRequest } from "../../API/Queries/useCreateCollectionRequest";
import { useUpdateCollectionRequest } from "../../API/Queries/useUpdateCollectionRequest";

const CollectionRequestTemplate = () => {
  const navigate = useNavigate();
  const { mutate: createRequest, isLoading } = useCreateCollectionRequest();
  const { mutate: updateRequest } = useUpdateCollectionRequest();
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [editingAssetIndex, setEditingAssetIndex] = useState(null);
  const [existingAsset, setExistingAsset] = useState(null);
  const [expandedActions, setExpandedActions] = useState({});
  const [expandedExpressions, setExpandedExpressions] = useState({});
  const [expandedAssetIndices, setExpandedAssetIndices] = useState({});
  const [toast, setToast] = useState({ type: "", message: "" });

  const location = useLocation();
  const prefillData = location.state?.prefillData;
  const isEditMode = location.pathname === "/dam/edit-request-collection";

  const [formData, setFormData] = useState(() => {
    const data = prefillData || {};

    return {
      id: data.id || "",
      name: data.name || "",
      allowEditAfterTerminalStatus: data.allowEditAfterTerminalStatus ?? true,
      disableAutoSubmit: data.disableAutoSubmit ?? false,
      disableAutoApproval: data.disableAutoApproval ?? false,
      callbackUrl: data.callbackUrl || "",
      callbackConfig: Array.isArray(data.callbackConfig)
        ? data.callbackConfig
        : [],

      assets: Array.isArray(data.assets)
        ? data.assets.map((asset) => {
            const parsedActions = Array.isArray(asset.actions)
              ? asset.actions.map((action) => ({
                  name: action.name || "",
                  initiatorType: action.initiatorType || "",
                  operation: action.operation || "",
                  mode: action.mode || "",
                  inputType: action.inputType || "",
                  provider: action.provider || "",
                  group: action.group || "",
                  actionGroup: action.actionGroup || "",
                  callbackUrl: action.callbackUrl || "",
                  eventData: action.eventData || "",
                  digitalAssetUpdateConfig: action.digitalAssetUpdateConfig || {
                    ruleConfig: {
                      name: "",
                      evaluationMode: "",
                      expressions: [],
                    },
                    serviceConfigName: "",
                    requiredAssetDefinitionNames: [],
                  },
                }))
              : [];

            return {
              id: asset.id || "",
              name: asset.name || "",
              group: asset.group || "",
              defaultActionGroup: asset.defaultActionGroup || "",
              retryConfig: asset.retryConfig || {
                currentRetryCount: "",
                maxRetryCount: "",
                purpose: "",
              },
              callbackConfig: Array.isArray(asset.callbackConfig)
                ? asset.callbackConfig
                : [],
              actions: parsedActions,
              isMandatory: asset.isMandatory ?? false,
              uniquenessCheck: asset.uniquenessCheck ?? false,
              expireAssetIfValidFromIsNull:
                asset.expireAssetIfValidFromIsNull ?? false,
              expireAssetIfValidToIsNull:
                asset.expireAssetIfValidToIsNull ?? false,
              matchActionsForReuse: asset.matchActionsForReuse ?? false,
            };
          })
        : [],
    };
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCallbackChange = (index, key, value) => {
    if (key === "onStatus") {
      const existingStatuses = formData.callbackConfig
        .filter((_, i) => i !== index)
        .map((c) => c.onStatus);

      if (existingStatuses.includes(value)) {
        setToast({
          type: "error",
          message: `"${value}" status is already selected.`,
        });
        return;
      }
    }

    const updated = [...formData.callbackConfig];
    updated[index][key] = value;
    setFormData({ ...formData, callbackConfig: updated });
  };

  const toggleActionExpand = (assetIndex, actionIndex) => {
    const key = `${assetIndex}-${actionIndex}`;
    setExpandedActions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleExpressionExpand = (assetIndex, actionIndex) => {
    const key = `${assetIndex}-${actionIndex}`;
    setExpandedExpressions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleAssetCollapse = (index) => {
    setExpandedAssetIndices((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const addCallbackConfig = () => {
    setFormData((prev) => ({
      ...prev,
      callbackConfig: [
        ...prev.callbackConfig,
        { callbackUrl: "", onStatus: "" },
      ],
    }));
  };

  const removeCallbackConfig = (index) => {
    const updated = formData.callbackConfig.filter((_, i) => i !== index);
    setFormData({ ...formData, callbackConfig: updated });
  };

  const handleAssetSave = (newAsset) => {
    setFormData((prev) => {
      const updatedAssets = [...prev.assets];
      if (editingAssetIndex !== null) {
        updatedAssets[editingAssetIndex] = newAsset;
      } else {
        updatedAssets.push(newAsset);
      }
      return { ...prev, assets: updatedAssets };
    });
    setShowAssetModal(false);
    setEditingAssetIndex(null);
    setExistingAsset(null);
    setErrors((prev) => ({ ...prev, assets: "" }));
  };

  const cleanObject = (obj) => {
    if (Array.isArray(obj)) {
      return obj
        .map(cleanObject)
        .filter((item) => item !== null && item !== undefined);
    } else if (typeof obj === "object" && obj !== null) {
      const cleaned = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0) &&
          !(typeof value === "object" && Object.keys(value).length === 0)
        ) {
          cleaned[key] = cleanObject(value);
        }
      });
      return Object.keys(cleaned).length > 0 ? cleaned : null;
    }
    return obj;
  };

  const handleSave = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "This field is required";
    if (formData.assets.length === 0)
      newErrors.assets = "At least one asset is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const rawPayload = {
      name: formData.name,
      allowEditAfterTerminalStatus: formData.allowEditAfterTerminalStatus,
      disableAutoSubmit: formData.disableAutoSubmit,
      disableAutoApproval: formData.disableAutoApproval,
      callbackConfig: formData.callbackConfig,
      assets: formData.assets.map((asset) => ({
        id: asset.id,
        name: asset.name,
        group: asset.group ?? null,
        isMandatory: asset.isMandatory,
        actions: asset.actions.map((action) => ({
          name: action.name,
          initiatorType: action.initiatorType,
          operation: action.operation,
          mode: action.mode,
          inputType: action.inputType,
          provider: action.provider,
          actionGroup: action.actionGroup,
          event: action.event ?? null,
          status: null,
          statusComment: null,
          disableStatusMigration: null,
          digitalAssetUpdateConfig: action.digitalAssetUpdateConfig ?? null,
          retryConfig: action.retryConfig ?? null,
        })),
        defaultActionGroup: asset.defaultActionGroup,
        uniquenessCheck: asset.uniquenessCheck ?? null,
        expireAssetIfValidFromIsNull:
          asset.expireAssetIfValidFromIsNull ?? null,
        expireAssetIfValidToIsNull: asset.expireAssetIfValidToIsNull ?? null,
        currentActionGroup: asset.currentActionGroup ?? null,
        callbackConfig: asset.callbackConfig ?? null,
        retryConfig: asset.retryConfig ?? null,
      })),
    };

    const cleanedPayload = cleanObject(rawPayload);

    const mutationFn = isEditMode ? updateRequest : createRequest;
    mutationFn(
      isEditMode
        ? { id: formData.id, payload: cleanedPayload }
        : cleanedPayload,
      {
        onSuccess: () => {
          setToast({
            type: "success",
            message: isEditMode
              ? "Collection Request Template updated successfully!"
              : "Collection Request Template created successfully!",
          });
          setTimeout(() => {
            navigate(-1);
          }, 1500);
        },

        onError: (err) => {
          console.error("Save failed", err);
          setToast({ type: "error", message: "Failed to save template." });
        },
      }
    );
  };

  const handleDiscard = () => {
    navigate(-1);
  };

  if (isLoading) return <Loader />;

  return (
    <>
      {toast.message && (
        <GeneralToast type={toast.type} message={toast.message} />
      )}
      <div className="crt-wrapper-container">
        <div className="crt-header-container">
          <div className="crt-header-box">
            <h3 className="text-base font-bold">
              {isEditMode
                ? "Edit Collection Request"
                : "Collection Request Template"}
            </h3>
          </div>
        </div>

        <div className="crt-content-container">
          <div className="crt-inner-wrapper">
            <div className="crt-input-group">
              <div
                className="crt-field-row"
                style={{ alignItems: "flex-start" }}
              >
                <label className="crt-label">
                  Name <span style={{ color: "red" }}>*</span>
                </label>
                <div className="crt-column">
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="crt-input-aligned"
                  />
                  {errors.name && (
                    <div className="crt-error-text">{errors.name}</div>
                  )}
                </div>
              </div>

              {[
                "allowEditAfterTerminalStatus",
                "disableAutoSubmit",
                "disableAutoApproval",
              ].map((name) => (
                <div className="crt-field-row" key={name}>
                  <label className="crt-label">
                    {name} <span style={{ color: "red" }}>*</span>
                  </label>
                  <label className="crt-toggle small">
                    <input
                      type="checkbox"
                      name={name}
                      checked={formData[name]}
                      onChange={handleChange}
                    />
                    <span className="crt-slider round"></span>
                  </label>
                </div>
              ))}

              <div className="crt-field-row">
                <label className="crt-label">Callback URL</label>
                <input
                  name="callbackUrl"
                  value={formData.callbackUrl}
                  onChange={handleChange}
                  className="crt-input-aligned"
                />
              </div>

              <div className="crt-field-row">
                <label className="crt-label">Callback Config</label>
                <button className="crt-add-btn" onClick={addCallbackConfig}>
                  +
                </button>
              </div>

              {formData.callbackConfig.map((entry, index) => (
                <div key={index} className="crt-config-wrapper">
                  <div className="crt-config-column">
                    <label className="crt-config-label">Callback URL</label>
                    <input
                      placeholder="Callback URL"
                      value={entry.callbackUrl}
                      onChange={(e) =>
                        handleCallbackChange(
                          index,
                          "callbackUrl",
                          e.target.value
                        )
                      }
                      className="crt-input-aligned"
                    />
                  </div>

                  <div className="crt-config-column">
                    <label className="crt-config-label">
                      onStatus <span style={{ color: "red" }}>*</span>
                    </label>
                    <div style={{ width: "100%" }}>
                      <Select
                        value={OnStatusOptions.find(
                          (option) => option.value === entry.onStatus
                        )}
                        onChange={(selected) =>
                          handleCallbackChange(
                            index,
                            "onStatus",
                            selected?.value || ""
                          )
                        }
                        options={OnStatusOptions.map((option) => ({
                          ...option,
                          isDisabled: formData.callbackConfig
                            .filter((_, i) => i !== index)
                            .map((c) => c.onStatus)
                            .includes(option.value),
                        }))}
                        classNamePrefix="react-select"
                      />
                    </div>
                  </div>

                  <div className="crt-config-remove">
                    <button
                      className="crt-remove-btn"
                      onClick={() => removeCallbackConfig(index)}
                    >
                      -
                    </button>
                  </div>
                </div>
              ))}

              <div
                className="crt-field-row"
                style={{ alignItems: "flex-start" }}
              >
                <label className="crt-label">
                  Assets <span style={{ color: "red" }}>*</span>
                </label>
                <div className="crt-inline-col">
                  <button
                    type="button"
                    className="crt-add-btn"
                    onClick={() => setShowAssetModal(true)}
                  >
                    +
                  </button>
                  {errors.assets && (
                    <div className="crt-error-text">{errors.assets}</div>
                  )}
                </div>
              </div>
              {formData.assets.length > 0 && (
                <div>
                  {formData.assets.map((asset, i) => {
                    const isExpanded = expandedAssetIndices[i];

                    return (
                      <div
                        key={i}
                        className="crt-asset-box"
                        style={{ display: "flex", gap: "16px" }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            onClick={() => toggleAssetCollapse(i)}
                            style={{
                              backgroundColor: "#f3f4f6",
                              padding: "10px 12px",
                              borderRadius: "8px",
                              cursor: "pointer",
                              fontWeight: "600",
                              width: "fit-content",
                              marginBottom: isExpanded ? "8px" : "0",
                            }}
                          >
                            {isExpanded ? "▼" : "▶"}{" "}
                            <span style={{ color: "#0e76a8" }}>Name:</span>{" "}
                            <span style={{ fontWeight: 400 }}>
                              {asset.name || "Unnamed Asset"}
                            </span>
                          </div>

                          {isExpanded && (
                            <div
                              style={{
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                padding: "12px",
                                backgroundColor: "#f9f9f9",
                                marginTop: "8px",
                              }}
                            >
                              {asset.id && (
                                <div className="crt-action-summary-item">
                                  <span>ID:</span>
                                  <span>{asset.id}</span>
                                </div>
                              )}
                              {asset.name && (
                                <div className="crt-action-summary-item">
                                  <span>Name:</span>
                                  <span>{asset.name}</span>
                                </div>
                              )}
                              {asset.defaultActionGroup && (
                                <div className="crt-action-summary-item">
                                  <span>Default Action Group:</span>
                                  <span>{asset.defaultActionGroup}</span>
                                </div>
                              )}
                              {asset.retryConfig?.maxRetryCount && (
                                <div className="crt-action-summary-item">
                                  <span>Max Retry Count:</span>
                                  <span>{asset.retryConfig.maxRetryCount}</span>
                                </div>
                              )}
                              {asset.retryConfig?.purpose && (
                                <div className="crt-action-summary-item">
                                  <span>Purpose:</span>
                                  <span>{asset.retryConfig.purpose}</span>
                                </div>
                              )}

                              {asset._touchedToggles?.length > 0 &&
                                asset._touchedToggles.map((key, idx) => (
                                  <div
                                    key={idx}
                                    className="crt-action-summary-item"
                                  >
                                    <span>{key}:</span>
                                    <span>{asset[key] ? "true" : "false"}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>

                        <div style={{ flex: 1, marginTop: "10px" }}>
                          <h4
                            style={{
                              marginBottom: "8px",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            Actions
                          </h4>

                          {asset.actions.map((action, j) => {
                            const isActionExpanded =
                              expandedActions[`${i}-${j}`];
                            return (
                              <div key={j} className="crt-action-box">
                                <h5
                                  style={{ fontWeight: 600, cursor: "pointer" }}
                                  onClick={() => toggleActionExpand(i, j)}
                                >
                                  {isActionExpanded ? "▼" : "▶"}{" "}
                                  <span style={{ color: "#0e76a8" }}>
                                    Name:
                                  </span>{" "}
                                  <span style={{ fontWeight: 400 }}>
                                    {action.name || "Unnamed Action"}
                                  </span>
                                </h5>

                                {isActionExpanded && (
                                  <>
                                    {Object.entries(action).map(
                                      ([key, val]) =>
                                        key !== "id" &&
                                        val &&
                                        typeof val !== "object" && (
                                          <div
                                            key={key}
                                            className="asset-action-summary-item"
                                          >
                                            <span className="asset-label">
                                              {key
                                                .replace(/([A-Z])/g, " $1")
                                                .replace(/^./, (str) =>
                                                  str.toUpperCase()
                                                )}
                                              :
                                            </span>
                                            <span className="asset-value">
                                              {val}
                                            </span>
                                          </div>
                                        )
                                    )}

                                    {(action.digitalAssetUpdateConfig
                                      ?.ruleConfig?.name ||
                                      action.digitalAssetUpdateConfig
                                        ?.ruleConfig?.evaluationMode ||
                                      (action.digitalAssetUpdateConfig
                                        ?.ruleConfig?.expressions?.length ??
                                        0) > 0 ||
                                      action.digitalAssetUpdateConfig
                                        ?.serviceConfigName) && (
                                      <div
                                        className="asset-action-summary-group"
                                        style={{ marginTop: "8px" }}
                                      >
                                        <strong
                                          style={{
                                            display: "block",
                                            marginBottom: "6px",
                                          }}
                                        >
                                          Digital Asset Update Config:
                                        </strong>

                                        {action.digitalAssetUpdateConfig
                                          .ruleConfig?.name && (
                                          <div className="asset-action-summary-item">
                                            <span className="asset-label">
                                              • RuleConfig Name:
                                            </span>
                                            <span className="asset-value">
                                              {
                                                action.digitalAssetUpdateConfig
                                                  .ruleConfig.name
                                              }
                                            </span>
                                          </div>
                                        )}

                                        {action.digitalAssetUpdateConfig
                                          .ruleConfig?.evaluationMode && (
                                          <div className="asset-action-summary-item">
                                            <span className="asset-label">
                                              • Evaluation Mode:
                                            </span>
                                            <span className="asset-value">
                                              {
                                                action.digitalAssetUpdateConfig
                                                  .ruleConfig.evaluationMode
                                              }
                                            </span>
                                          </div>
                                        )}

                                        {action.digitalAssetUpdateConfig
                                          .ruleConfig?.expressions?.length >
                                          0 && (
                                          <div className="asset-action-summary-item">
                                            <strong
                                              style={{ cursor: "pointer" }}
                                              onClick={() =>
                                                toggleExpressionExpand(i, j)
                                              }
                                            >
                                              {expandedExpressions[`${i}-${j}`]
                                                ? "▼"
                                                : "▶"}{" "}
                                              Expressions
                                            </strong>

                                            {expandedExpressions[
                                              `${i}-${j}`
                                            ] && (
                                              <div
                                                style={{ marginTop: "0.5rem" }}
                                              >
                                                {action.digitalAssetUpdateConfig.ruleConfig.expressions.map(
                                                  (exp, k) => (
                                                    <div
                                                      key={k}
                                                      className="expression-block"
                                                    >
                                                      <div>
                                                        Type: {exp.type}
                                                      </div>
                                                      <div>LHS: {exp.lhs}</div>
                                                      <div>RHS: {exp.rhs}</div>
                                                      {exp.validationFailureMessage && (
                                                        <div>
                                                          Msg:{" "}
                                                          {
                                                            exp.validationFailureMessage
                                                          }
                                                        </div>
                                                      )}
                                                    </div>
                                                  )
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        )}

                                        {action.digitalAssetUpdateConfig
                                          .serviceConfigName && (
                                          <div className="asset-action-summary-item">
                                            <span className="asset-label">
                                              • Service Config Name:
                                            </span>
                                            <span className="asset-value">
                                              {
                                                action.digitalAssetUpdateConfig
                                                  .serviceConfigName
                                              }
                                            </span>
                                          </div>
                                        )}

                                        {action.event?.callbackUrl && (
                                          <div className="asset-action-summary-item">
                                            <span className="asset-label">
                                              Callback URL:
                                            </span>
                                            <span className="asset-value">
                                              {action.event.callbackUrl}
                                            </span>
                                          </div>
                                        )}

                                        {Array.isArray(
                                          action.event?.eventData
                                        ) &&
                                          action.event.eventData.length > 0 && (
                                            <div className="asset-action-summary-item">
                                              <span className="asset-label">
                                                Event Data:
                                              </span>
                                              <div className="asset-value">
                                                {action.event.eventData.map(
                                                  (entry, idx) => {
                                                    const {
                                                      onStatus,
                                                      entity,
                                                      entityId,
                                                    } = entry || {};
                                                    return (
                                                      (onStatus ||
                                                        entity ||
                                                        entityId) && (
                                                        <div
                                                          key={idx}
                                                          style={{
                                                            marginBottom: "6px",
                                                          }}
                                                        >
                                                          {onStatus && (
                                                            <div>
                                                              • onStatus:{" "}
                                                              {onStatus}
                                                            </div>
                                                          )}
                                                          {entity && (
                                                            <div>
                                                              • entity: {entity}
                                                            </div>
                                                          )}
                                                          {entityId && (
                                                            <div>
                                                              • entityId:{" "}
                                                              {entityId}
                                                            </div>
                                                          )}
                                                        </div>
                                                      )
                                                    );
                                                  }
                                                )}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "10px",
                            fontSize: "16px",
                            marginTop: "10px",
                          }}
                        >
                          <FaEdit
                            title="Edit"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setEditingAssetIndex(i);
                              setExistingAsset(formData.assets[i]);
                              setShowAssetModal(true);
                            }}
                          />
                          <FaTrash
                            title="Delete"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                assets: prev.assets.filter(
                                  (_, index) => index !== i
                                ),
                              }));
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="crt-button-container">
                <button className="crt-save-btn" onClick={handleSave}>
                  {isEditMode ? "Update" : "Save Changes"}
                </button>

                <button className="crt-discard-btn" onClick={handleDiscard}>
                  Discard
                </button>
              </div>
            </div>
          </div>
        </div>

        {showAssetModal && (
          <AssetsModal
            onClose={() => {
              setShowAssetModal(false);
              setEditingAssetIndex(null);
              setExistingAsset(null);
            }}
            onSaveAsset={handleAssetSave}
            existingAsset={existingAsset}
          />
        )}
      </div>
    </>
  );
};

export default CollectionRequestTemplate;
